// Project Work data layer (Phase 1). project_id auto-assigned by trigger.
// 2026-05-28: AFB-0005 (sort), AFB-0006 (AM display), AFB-0007 (margin auto-compute).
//   - useProjects now accepts a `sort` param.
//   - createProject / updateProject no longer compute margin_amount or margin_pct
//     client-side; the BEFORE INSERT/UPDATE trigger trg_projects_compute_margin
//     does that authoritatively (migration auto_compute_project_margin).
//   - Account Manager for each project is resolved via a flat-fetch over the
//     unique facility_ids and joined to employees by account_manager_eid (the
//     FK constraint name `facilities_account_manager_eid_fkey` does not exist
//     in the DB, so PostgREST embed by FK is not available here).
import { buildRepeat, type RepeatSource, type RepeatableLine } from '../lib/repeat-project';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import type { Project, ProjectRow, ProjectStage, UpdateProjectInput } from '../../../shared/types';
import type { ProjectSort } from '../types';
import { errorMessage } from '../../../shared/errors';

interface ProjectRowFromDb extends Project {
  facilities: { facility_name: string | null; market_region: string | null; account_manager_eid: string | null; status: string | null } | null;
  accounts: { account_name: string | null } | null;
  partner: { provider_id: string; provider_name: string | null } | null;
  creator: { employee_name_first: string | null; employee_name_last: string | null } | null;
}

const PROJECT_SELECT = `
  project_id, account_id, facility_id, account_name, facility_name,
  service_type_raw, start_date, completion_date, notes,
  assigned_partner_id, bill_amount, pay_amount, margin_amount,
  due_date, created_by_eid, stage, category_raw, created_date,
  margin_status, margin_pct, updated_by_eid, created_at, updated_at,
  ready_to_invoice_at, pending_po_at, invoiced_at, invoiced_by_eid,
  awaiting_am_input_at, awaiting_am_input_by_eid,
  awaiting_am_input_reason, awaiting_am_input_resolved_at,
  awaiting_schedule, special_pay, reason_note, event_class, reason_code, source,
  project_services (
    service_line_id, project_id, line_role, service_code, scope_text, scope_qty, scope_unit,
    client_rate, sub_rate, line_margin_pct, assigned_partner_id,
    service_date_planned, service_date_actual, timing_state, line_order, notes,
    service_catalog ( display_name, division_id ),
    partner_payments ( payment_id )
  ),
  partner_payments ( payment_id ),
  facilities ( facility_name, market_region, account_manager_eid, status ),
  accounts   ( account_name ),
  partner:service_providers!projects_assigned_partner_id_fkey (
    provider_id, provider_name:partner_name
  ),
  creator:employees!projects_created_by_eid_fkey (
    employee_name_first, employee_name_last
  )
`;

interface AmRecord {
  employee_id: string;
  employee_name_first: string | null;
  employee_name_last: string | null;
}

// Sort spec → Supabase .order() call. We do all sorts server-side so pagination
// (when added) stays correct.
function applySort<T extends { order: (col: string, opts?: { ascending?: boolean; nullsFirst?: boolean }) => T }>(
  q: T,
  sort: ProjectSort,
): T {
  switch (sort) {
    case 'created_asc':
      return q.order('created_at', { ascending: true, nullsFirst: false });
    case 'start_desc':
      return q.order('start_date', { ascending: false, nullsFirst: false });
    case 'due_asc':
      return q.order('due_date', { ascending: true, nullsFirst: false });
    case 'bill_desc':
      return q.order('bill_amount', { ascending: false, nullsFirst: false });
    case 'created_desc':
    default:
      return q.order('created_at', { ascending: false, nullsFirst: false });
  }
}

// Hydrate facilities.account_manager with employee names via a single IN-lookup.
// Returns a new array; caller can replace state in one shot.
async function attachAccountManagers(rows: ProjectRowFromDb[]): Promise<ProjectRow[]> {
  const eids = Array.from(
    new Set(
      rows
        .map((r) => r.facilities?.account_manager_eid ?? null)
        .filter((eid): eid is string => !!eid),
    ),
  );
  let amMap: Map<string, AmRecord> = new Map();
  if (eids.length > 0) {
    const { data: emps, error: empErr } = await supabase
      .from('v_employees_am')
      .select('employee_id, employee_name_first, employee_name_last')
      .in('employee_id', eids);
    if (empErr) {
      // Non-fatal — render the cards without AM rather than blocking the list.
      console.warn('[project-work] AM lookup failed', empErr);
    } else if (emps) {
      amMap = new Map((emps as AmRecord[]).map((e) => [e.employee_id, e]));
    }
  }
  return rows.map((r) => {
    const eid = r.facilities?.account_manager_eid ?? null;
    const am = eid ? (amMap.get(eid) ?? null) : null;
    return {
      ...r,
      facilities: r.facilities
        ? { ...r.facilities, account_manager: am }
        : null,
    } as ProjectRow;
  });
}

interface QueryFilter {
  createdBy?: string; facilityId?: string; accountId?: string;
  /**
   * A work view (item #650). `facilityIds` null = every card. With `orCreatedBy`
   * a card is also in the view when that person created it (D4: mine = I created
   * it or it is on one of my buildings). The building list comes from
   * work_scope_facility_ids(); this hook never works it out.
   */
  scope?: { facilityIds: string[] | null; orCreatedBy?: string | null } | null;
  serviceType?: string; stages?: ProjectStage[] | string[];
  startDateFrom?: string; startDateTo?: string;
  sort?: ProjectSort;
}

export interface ProjectsDataResult {
  data: ProjectRow[]; isLoading: boolean; error: string | null;
  lastUpdated: Date | null; refetch: () => void;
}

export function useProjects(filter: QueryFilter = {}): ProjectsDataResult {
  const [data, setData] = useState<ProjectRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [tick, setTick] = useState(0);
  const cancelledRef = useRef(false);
  useEffect(() => { cancelledRef.current = false; return () => { cancelledRef.current = true; }; }, []);
  const sort: ProjectSort = filter.sort ?? 'created_desc';
  const filterKey = JSON.stringify({
    createdBy: filter.createdBy ?? null, facilityId: filter.facilityId ?? null,
    accountId: filter.accountId ?? null, serviceType: filter.serviceType ?? null,
    stages: filter.stages ?? null,
    scope: filter.scope ?? null,
    startDateFrom: filter.startDateFrom ?? null, startDateTo: filter.startDateTo ?? null,
    sort,
  });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading((prev) => (lastUpdated === null ? true : prev));
      setError(null);
      try {
        let q = supabase.from('projects').select(PROJECT_SELECT);
        if (filter.createdBy) q = q.eq('created_by_eid', filter.createdBy);
        if (filter.scope && filter.scope.facilityIds) {
          const ids = filter.scope.facilityIds;
          if (filter.scope.orCreatedBy) {
            const parts = [`created_by_eid.eq.${filter.scope.orCreatedBy}`];
            if (ids.length > 0) parts.push(`facility_id.in.(${ids.join(',')})`);
            q = q.or(parts.join(','));
          } else if (ids.length > 0) {
            q = q.in('facility_id', ids);
          } else {
            q = q.eq('facility_id', '__none__');
          }
        }
        if (filter.facilityId) q = q.eq('facility_id', filter.facilityId);
        if (filter.accountId) q = q.eq('account_id', filter.accountId);
        // Legacy filter — matches the historic free text on the audit column.
        // The controlled equivalent filters project_services.service_code.
        if (filter.serviceType) q = q.eq('service_type_raw', filter.serviceType);
        if (filter.stages && filter.stages.length > 0) q = q.in('stage', filter.stages as string[]);
        if (filter.startDateFrom) q = q.gte('start_date', filter.startDateFrom);
        if (filter.startDateTo) q = q.lte('start_date', filter.startDateTo);
        q = applySort(q, sort);
        const { data: rows, error: e } = await q;
        if (cancelled) return;
        if (e) throw e;
        const hydrated = await attachAccountManagers((rows ?? []) as unknown as ProjectRowFromDb[]);
        if (cancelled) return;
        setData(hydrated);
        setLastUpdated(new Date());
      } catch (err) {
        if (cancelled) return;
        console.error('[project-work] load failed', err);
        setError(errorMessage(err, 'Failed to load projects.'));
      } finally { if (!cancelled) setIsLoading(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, filterKey]);
  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return useMemo(() => ({ data, isLoading, error, lastUpdated, refetch }),
    [data, isLoading, error, lastUpdated, refetch]);
}

/** One project as its screen reads it (also Ask AI's project card, item #944). */
export async function fetchProject(projectId: string): Promise<ProjectRow | null> {
  const { data: row, error: e } = await supabase.from('projects').select(PROJECT_SELECT).eq('project_id', projectId).maybeSingle();
  if (e) throw e;
  if (!row) return null;
  const hydrated = await attachAccountManagers([row as unknown as ProjectRowFromDb]);
  return hydrated[0] ?? null;
}

export function useProject(projectId: string | null) {
  const [data, setData] = useState<ProjectRow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!projectId) { setData(null); setIsLoading(false); return; }
    let cancelled = false;
    (async () => {
      setIsLoading(true); setError(null);
      try {
        const row = await fetchProject(projectId);
        if (cancelled) return;
        setData(row);
      } catch (err) {
        if (cancelled) return;
        setError(errorMessage(err, 'Failed to load the project.'));
      } finally { if (!cancelled) setIsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [projectId, tick]);
  return { data, isLoading, error, refetch: () => setTick((n) => n + 1) };
}

// AFB-0007 (2026-05-28): margin_amount and margin_pct are auto-computed by the
// BEFORE INSERT/UPDATE trigger trg_projects_compute_margin on the projects
// table. The client never sends those columns — letting the trigger be the
// single source of truth avoids semantic drift (decimal vs percentage form).
//
// Run B Phase 2 (2026-08-18): the single-line createProject() that used to sit
// here is gone. It could only ever write ONE service line, and a project is now
// a set of them. createProjectWithLines() at the bottom of this file replaces
// it and handles the one-line case as the ordinary case. Two create paths would
// have been two chances to disagree about what a project is.

// ---------------------------------------------------------------------------
// project_services — the controlled service line(s) under a project.
// Run A Phase 1.2. service_line_id is assigned by trg_project_services_assign_id,
// and scope_unit defaults from service_catalog.default_unit in that same trigger,
// so neither is ever sent from here.
// ---------------------------------------------------------------------------
export interface ProjectServiceLineInput {
  /** 'service' (default) or 'chargeback' — see EntryLine.line_role. */
  line_role?: string;
  /** NULL on a chargeback line: a chargeback is not a service. */
  service_code: string | null;
  scope_text?: string | null;
  scope_qty?: number | null;
  client_rate?: number | null;
  sub_rate?: number | null;
  assigned_partner_id?: string | null;
  service_date_planned?: string | null;
  service_date_actual?: string | null;
  line_order?: number | null;
  notes?: string | null;
}

/**
 * Insert a new service line, or update an existing one when `serviceLineId` is
 * given. Used by the create form (insert) and by the project detail line editor
 * (update) — the detail edit is why this has to accept an id.
 *
 * ⚠️ Run C Phase 2, 2026-08-19 — THIS IS A PATCH, NOT A ROW REWRITE.
 *
 * It used to build the body with `line.<col> ?? null` for every column, so an
 * UPDATE that named only the columns it cared about silently NULLed the ones it
 * did not. Classifying a line by `service_code` alone would have erased its
 * rates, its sub and both its dates; the detail page's editor, which never knew
 * about `scope_qty` or `notes`, erased those on every save.
 *
 * Now a key that is `undefined` is simply not written. On INSERT that is
 * identical to before (the column default is NULL and the entry screen passes
 * every field anyway); on UPDATE it is the difference between editing a line and
 * overwriting it. Pass `null` explicitly to clear a column on purpose.
 *
 * The fix lives here rather than in a third caller-side write path, because a
 * third path that knows a different subset of the row is how this happened.
 */
export async function saveProjectServiceLine(
  projectId: string,
  line: ProjectServiceLineInput,
  serviceLineId?: string | null,
): Promise<string | null> {
  const body: Record<string, unknown> = {};
  const put = (col: keyof ProjectServiceLineInput) => {
    if (line[col] !== undefined) body[col] = line[col];
  };
  // service_code is required by the table, so it is always written.
  body.service_code = line.service_code;
  put('line_role');
  put('scope_text');
  put('scope_qty');
  put('client_rate');
  put('sub_rate');
  put('assigned_partner_id');
  put('service_date_planned');
  put('service_date_actual');
  put('notes');
  if (line.line_order != null) body.line_order = line.line_order;
  if (serviceLineId) {
    const { data, error } = await supabase
      .from('project_services')
      .update(body)
      .eq('service_line_id', serviceLineId)
      .select('service_line_id');
    if (error) throw error;
    if (!data || data.length === 0) {
      throw new Error(
        "That service change didn't save — you may not have permission to edit this project's service lines.",
      );
    }
    return serviceLineId;
  }
  const { data, error } = await supabase
    .from('project_services')
    .insert({ ...body, project_id: projectId })
    .select('service_line_id')
    .single();
  if (error) throw error;
  return (data as { service_line_id: string } | null)?.service_line_id ?? null;
}

/**
 * Remove ONE service line. Run C Phase 2.
 *
 * `project_services` has a `block_delete` trigger, so there is no bare DELETE to
 * fall back to — this RPC is the only path. It snapshots the row into
 * `deletion_requests` before removing it and refuses outright when the project
 * has already minted a subcontractor payment.
 */
export async function deleteProjectServiceLine(
  serviceLineId: string,
  reason: string,
): Promise<void> {
  const trimmed = reason.trim();
  if (!trimmed) throw new Error('A reason is required to remove a service line.');
  const { error } = await supabase.rpc('delete_project_service_line', {
    p_service_line_id: serviceLineId,
    p_reason: trimmed,
  });
  if (error) throw error;
}

export async function updateProject(projectId: string, patch: UpdateProjectInput, updatedByEid: string): Promise<void> {
  // margin_amount / margin_pct intentionally omitted — see note above.
  const payload: Record<string, unknown> = { ...patch, updated_by_eid: updatedByEid };
  // AFB-0036/0037 — surface zero-row writes. A `.update()` that matches no rows
  // under RLS returns success with no error (a silent no-op that looked like a
  // "revert" to the field). `.select()` lets us detect that and raise so the UI
  // toasts instead of silently discarding the edit.
  const { data, error } = await supabase
    .from('projects')
    .update(payload)
    .eq('project_id', projectId)
    .select('project_id');
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error(
      "That change didn't save — you may not have permission to edit this project, or it no longer exists.",
    );
  }
}

// AFB-0022 #1 — delete a project entry (created in error). projects has a
// block_delete trigger, so a plain DELETE is rejected; deletion goes through
// the SECURITY DEFINER delete_project RPC, which snapshots the row to
// deletion_requests and performs the sanctioned hard delete. A reason is
// required for the audit trail.
//
// TCSD-0155 — TWO DOORS, different widths:
//   * UNPAID project → the creator, national_am, finance, executive, superuser.
//   * PAID project   → finance, executive, superuser ONLY, and the payment
//     record is deleted WITH the project, each fully snapshotted and
//     cross-referenced.
// ⛔ Executive is resolved by is_executive() (employees.employee_type = 'Exec',
// TCSD-0131), never by user_profiles.role — no row has ever carried that string.
// Refusals arrive as TCS_PROJ_NOT_AUTHORIZED / TCS_PROJ_PAID_DELETE_NOT_AUTHORIZED
// and the paid one NAMES the payment, so surface it with rpcErrorMessage.
export async function deleteProject(projectId: string, reason: string): Promise<void> {
  const trimmed = reason.trim();
  if (!trimmed) throw new Error('A reason is required to delete a project.');
  const { error } = await supabase.rpc('delete_project', {
    p_project_id: projectId,
    p_reason: trimmed,
  });
  if (error) throw error;
}

// AFB-0021 — the sub payment auto-minted for a project (if any).
export interface ProjectPayment {
  payment_id: string;
  status: string | null;
  amount: number | null;
}
export async function fetchProjectPayment(projectId: string): Promise<ProjectPayment | null> {
  const { data, error } = await supabase
    .from('partner_payments')
    .select('payment_id, status, amount')
    .eq('reimburses_project_id', projectId)
    .maybeSingle();
  if (error) throw error;
  return (data as ProjectPayment | null) ?? null;
}

// ---------------------------------------------------------------------------
// Run C Phase 4 — per-line subcontractor payment.
//
// A project with two or more service lines can never be paid from its
// project-level amount (TCSD-0059). This is the path that makes that refusal
// survivable: each line pays its OWN sub, its own rate, on its own date.
// ---------------------------------------------------------------------------
export interface ProjectLinePayment {
  service_line_id: string;
  project_id: string;
  line_order: number | null;
  service_label: string | null;
  provider_id: string | null;
  provider_name: string | null;
  amount: number | null;
  pay_date: string | null;
  existing_payment_id: string | null;
  existing_payment_status: string | null;
  project_payment_id: string | null;
  special_pay: boolean | null;
  mintable: boolean | null;
  blocked_reason: string | null;
}

/**
 * The preview: per line, what would be paid and — when it cannot be — why not,
 * in the same words and the same gate order the RPC uses. Read the whole set
 * before paying any of it.
 */
export function useProjectLinePayments(projectId: string | null, version = 0) {
  const [rows, setRows] = useState<ProjectLinePayment[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) {
      setRows([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error: err } = await supabase
        .from('v_project_line_payment_preview')
        .select(
          'service_line_id, project_id, line_order, service_label, provider_id, provider_name, amount, pay_date, existing_payment_id, existing_payment_status, project_payment_id, special_pay, mintable, blocked_reason',
        )
        .eq('project_id', projectId)
        .order('line_order');
      if (cancelled) return;
      if (err) {
        // RLS may hide partner_payments from a non-finance reader; the panel
        // degrades to "can't show payment state" rather than claiming there is
        // none. An empty result that means "not allowed to look" is the most
        // dangerous kind of empty.
        setError(errorMessage(err, 'Could not read the payment preview.'));
        setRows([]);
        return;
      }
      setError(null);
      setRows((data ?? []) as ProjectLinePayment[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, version]);

  return { rows, error };
}

/**
 * Pay ONE service line. Idempotent on the line: calling it twice returns the
 * same payment_id and inserts nothing the second time.
 */
export async function mintProjectServiceLinePayment(
  serviceLineId: string,
  override = true,
): Promise<string | null> {
  const { data, error } = await supabase.rpc('mint_project_service_line_payment', {
    p_service_line_id: serviceLineId,
    p_override: override,
  });
  if (error) throw error;
  return (data as string | null) ?? null;
}

// AFB-0021 — manual mint from the review queue (special_pay / late assignment).
// p_override lets finance/full-AM mint a special_pay project; the RPC enforces
// every other guard (completion date, positive pay, assigned sub, no dup).
export async function mintProjectSubPayment(
  projectId: string,
  override = true,
): Promise<string | null> {
  const { data, error } = await supabase.rpc('mint_project_sub_payment', {
    p_project_id: projectId,
    p_override: override,
  });
  if (error) throw error;
  return (data as string | null) ?? null;
}

// Advance a project through the billing pipeline. Sets the relevant timestamp
// when transitioning into Ready_to_Invoice or Invoiced. Caller must pass the
// acting employee_id so we can stamp invoiced_by_eid for finance moves and
// updated_by_eid for the audit trail.
export async function updateProjectStage(
  projectId: string,
  stage: ProjectStage,
  updatedByEid: string,
  options?: { invoicedByEid?: string },
): Promise<void> {
  const now = new Date().toISOString();
  const payload: Record<string, unknown> = {
    stage,
    updated_by_eid: updatedByEid,
  };
  if (stage === 'Ready_to_Invoice') {
    payload.ready_to_invoice_at = now;
  } else if (stage === 'Pending_PO') {
    // Finance has sent the client the billing notice and is waiting on their
    // PO number. Stamp when we entered the waiting state for a "days waiting".
    payload.pending_po_at = now;
  } else if (stage === 'Invoiced') {
    payload.invoiced_at = now;
    payload.invoiced_by_eid = options?.invoicedByEid ?? updatedByEid;
  }
  // 'Completed' deliberately does NOT stamp completion_date in this payload.
  // The comment here used to say "if missing" while the code stamped it
  // unconditionally, so every Invoiced -> Completed advance overwrote the real
  // service date with today's. That destroyed 12 real dates between 2026-08-12
  // and 2026-08-18 alone (recovered from audit_log.old_data on 2026-08-19).
  // The fill-if-missing is a separate, guarded write below.
  // AFB-0036/0037 — same zero-row guard as updateProject(): a creator advancing
  // their own card (Draft -> Ready_to_Invoice) now passes RLS, but if any future
  // gap matches no rows we raise instead of silently swallowing the advance.
  const { data, error } = await supabase
    .from('projects')
    .update(payload)
    .eq('project_id', projectId)
    .select('project_id');
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error(
      "Couldn't advance this project — you may not have permission, or it no longer exists.",
    );
  }

  // Fill-if-missing, done as its own write with `.is('completion_date', null)`
  // so the guard lives in the WHERE clause rather than in a value this client
  // read some seconds ago. A project completed months ago and only now cleared
  // through billing keeps the date the work actually happened; one that never
  // carried a date gets today's, which is the best we know.
  if (stage === 'Completed') {
    const { error: fillErr } = await supabase
      .from('projects')
      .update({ completion_date: now.slice(0, 10) })
      .eq('project_id', projectId)
      .is('completion_date', null);
    if (fillErr) throw fillErr;
  }
}

// AFB-0009 — Send a Ready_to_Invoice card back to the AM for missing data.
// Finance/exec/superuser action (the screen shows it to showsSendBackToAm).
// Stamps who/when/why and clears any prior resolution timestamp. The database
// allows it: open_edit_all_employees lets every employee update a project
// (the old finance_mark_invoiced limit is gone — checked 2026-10-21).
export async function sendProjectBackToAm(
  projectId: string,
  reason: string,
  byEid: string,
): Promise<void> {
  const now = new Date().toISOString();
  const payload: Record<string, unknown> = {
    stage: 'Awaiting_AM_Input',
    awaiting_am_input_at: now,
    awaiting_am_input_by_eid: byEid,
    awaiting_am_input_reason: reason,
    awaiting_am_input_resolved_at: null,
    updated_by_eid: byEid,
  };
  // Same zero-row guard as updateProject(): a refused UPDATE is 0 rows and no
  // error, so the screen (and Ask AI's card) said it was done when it was not.
  const { data, error } = await supabase
    .from('projects')
    .update(payload)
    .eq('project_id', projectId)
    .select('project_id');
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("That didn't save — you may not have permission to move this project, or it no longer exists.");
  }
}

// AFB-0009 — AM resubmits a bounced-back card. Moves it to Ready_to_Invoice
// (the natural target — finance was reviewing it for invoicing) and stamps the
// resolution time. Covered by the existing am_update_projects RLS policy for
// the AM-on-facility, plus national_am/superuser via full authority.
export async function resolveAwaitingAmInput(
  projectId: string,
  byEid: string,
): Promise<void> {
  const now = new Date().toISOString();
  const payload: Record<string, unknown> = {
    stage: 'Ready_to_Invoice',
    ready_to_invoice_at: now,
    awaiting_am_input_resolved_at: now,
    updated_by_eid: byEid,
  };
  // Same zero-row guard as updateProject(): a refused UPDATE is 0 rows and no
  // error, so the screen (and Ask AI's card) said it was done when it was not.
  const { data, error } = await supabase
    .from('projects')
    .update(payload)
    .eq('project_id', projectId)
    .select('project_id');
  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error("That didn't save — you may not have permission to move this project, or it no longer exists.");
  }
}

export interface FacilityOption { facility_id: string; facility_name: string | null; account_id: string | null; market_region: string | null; }
export interface ProviderOption { provider_id: string; provider_name: string | null; provider_status: string | null; }

export function useFacilityOptions(): { facilities: FacilityOption[]; isLoading: boolean; error: string | null; } {
  const [facilities, setFacilities] = useState<FacilityOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: e } = await supabase.from('active_facilities').select('facility_id, facility_name, account_id, market_region').order('facility_name');
        if (cancelled) return;
        if (e) throw e;
        setFacilities((data ?? []) as FacilityOption[]);
      } catch (err) {
        if (cancelled) return;
        setError(errorMessage(err, 'Failed to load facilities.'));
      } finally { if (!cancelled) setIsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);
  return { facilities, isLoading, error };
}

// ⛔ RETIRED 2026-09-02 — `useProviderOptions` lived here and fetched
// `service_providers` with `.eq('provider_status','Active')`. That is a PICKER
// filter applied to a list that also had to RESOLVE saved values (TCSD-0163),
// so 81 project service lines pointing at a Merged or Inactive sub rendered as
// "Not assigned yet" — a sub WAS recorded and the screen said otherwise. It also
// offered 13 back-office, staffing and professional-services vendors as the
// answer to "who cleaned this building".
//
// Both dropdowns now use `SmartSelect`, which fetches status-agnostically,
// filters `provider_class='Field_Service'`, and narrows only what may be CHOSEN
// (`isSelectable`), leaving saved values resolvable.
//
// Do not reintroduce a status-filtered provider fetch here. `ProviderOption`
// stays above because the sourcing module has its own, separate hook.

// ---------------------------------------------------------------------------
// AFB-0022 #4 — Assign an account manager to a facility.
//
// account_manager_eid on facilities is the source of truth for the project's
// AM (account_manager_name can drift). This is the write path behind the
// "Assign AM" affordance shown when a project's facility has no AM.
//
// IMPORTANT (RLS / governance): account_manager_eid is a PROTECTED column —
// enforce_facilities_protected_cols() only lets superuser/executive/finance
// (or a writer that set tcs.rcp_apply='on') change it, AND an RLS UPDATE
// policy must also permit the row. Net effect of a direct UPDATE today:
//   superuser  → works (RLS superuser_all + trigger allows)
//   national_am→ blocked by the protected-cols trigger
//   executive  → blocked by RLS (no executive UPDATE policy)
// So this direct write only succeeds for superuser. A sanctioned
// assign_facility_am() RPC is proposed in the round-2 cowork report so
// national_am/executive can assign too — flagged for Ben to approve.
export async function assignFacilityAm(facilityId: string, eid: string): Promise<void> {
  if (!facilityId || !eid) throw new Error('A facility and an account manager are required.');
  // 2026-06-04 (Ben approved): route through the sanctioned assign_facility_am()
  // RPC (SECURITY DEFINER). It validates has_full_am_authority(), sets
  // tcs.rcp_apply='on' to clear the protected-cols guard, and writes the SSOT
  // (account_manager_eid) + denormalized name — so national_am + executive can
  // assign too, not just superuser (the prior direct UPDATE only worked for SU).
  const { error } = await supabase.rpc('assign_facility_am', {
    p_facility_id: facilityId,
    p_eid: eid,
  });
  if (error) throw error;
}

export interface AmEligibleEmployee { employee_id: string; name: string; }

// AM-eligible employees for the assign picker. employees.is_am_eligible is the
// canonical flag (8 AM-eligible as of 2026-09-02 — measure it, do not quote
// this number). Flat-fetch, ordered by name.
//
// Binds to v_employee_picker: Active AND pay_type='Salaried' AND app_role IS
// NOT NULL (TCSD-0152). PICKERS EXCLUDE, RESOLVERS RESOLVE (TCSD-0129) — this
// asks "who could I choose", so it must not offer temps or a fractional CFO.
// ⛔ DO NOT WIDEN THIS BACK TO v_employees_am. That view is the FULL roster and
// binding a dropdown to it is the mistake this repoint corrects — the third
// time it has been made.
// employment_status is NOT selected or filtered here: every row in
// v_employee_picker is Active by definition, and the view has no such column,
// so naming it fails at RUNTIME (PostgREST), which tsc cannot catch.
// Verified 2026-09-02 before repointing: all 8 is_am_eligible employees are
// inside the picker gate, so this narrowing drops no real AM.
export function useAmEligibleEmployees(): {
  employees: AmEligibleEmployee[];
  isLoading: boolean;
  error: string | null;
} {
  const [employees, setEmployees] = useState<AmEligibleEmployee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: e } = await supabase
          .from('v_employee_picker')
          .select('employee_id, display_name, is_am_eligible')
          .eq('is_am_eligible', true)
          .order('display_name');
        if (cancelled) return;
        if (e) throw e;
        type Row = {
          employee_id: string;
          display_name: string | null;
        };
        setEmployees(
          ((data ?? []) as Row[]).map((r) => ({
            employee_id: r.employee_id,
            name: r.display_name ?? r.employee_id,
          })),
        );
      } catch (err) {
        if (cancelled) return;
        setError(errorMessage(err, 'Failed to load account managers.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return { employees, isLoading, error };
}

// ---------------------------------------------------------------------------
// Project activity log (project_notes) — 2026-06-02.
// Append-only free-text log anyone can add to ("called customer 1 Jun,
// received PO 2 Jun"). System stamps (submitter/editors/approvers/timestamps)
// live on the projects row; this captures the human follow-up narrative.
// ---------------------------------------------------------------------------
export interface ProjectNote {
  note_id: string;
  project_id: string;
  author_eid: string | null;
  body: string;
  created_at: string;
  author?: { employee_name_first: string | null; employee_name_last: string | null } | null;
}

export function useProjectNotes(projectId: string | null) {
  const [notes, setNotes] = useState<ProjectNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!projectId) { setNotes([]); setIsLoading(false); return; }
    let cancelled = false;
    (async () => {
      setIsLoading(true); setError(null);
      try {
        const { data, error: e } = await supabase
          .from('project_notes')
          .select(
            'note_id, project_id, author_eid, body, created_at, author:employees!project_notes_author_eid_fkey(employee_name_first, employee_name_last)',
          )
          .eq('project_id', projectId)
          .order('created_at', { ascending: false });
        if (cancelled) return;
        if (e) throw e;
        setNotes((data ?? []) as unknown as ProjectNote[]);
      } catch (err) {
        if (cancelled) return;
        setError(errorMessage(err, 'Failed to load the activity log.'));
      } finally { if (!cancelled) setIsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [projectId, tick]);
  return { notes, isLoading, error, refetch: () => setTick((n) => n + 1) };
}

export async function addProjectNote(projectId: string, body: string, authorEid: string): Promise<void> {
  const { error } = await supabase
    .from('project_notes')
    .insert({ project_id: projectId, body, author_eid: authorEid });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// useEmployeeNames — resolve a handful of employee EIDs to display names for
// the activity log's system events (invoiced-by, sent-back-by, last editor).
// Flat-fetch IN-lookup, the standard pattern here (no PostgREST FK embed). The
// query key is the sorted distinct EID list so it only refetches when the set
// actually changes. Falls back to the raw EID if a name isn't found.
// ---------------------------------------------------------------------------
export function useEmployeeNames(eids: (string | null | undefined)[]): Record<string, string> {
  const key = useMemo(
    () => [...new Set(eids.filter((e): e is string => !!e))].sort().join(','),
    [eids],
  );
  const [names, setNames] = useState<Record<string, string>>({});
  useEffect(() => {
    const ids = key ? key.split(',') : [];
    if (ids.length === 0) {
      setNames({});
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('v_employees_am')
        .select('employee_id, employee_name_first, employee_name_last')
        .in('employee_id', ids);
      if (cancelled || error || !data) return;
      const map: Record<string, string> = {};
      for (const e of data as Array<{
        employee_id: string;
        employee_name_first: string | null;
        employee_name_last: string | null;
      }>) {
        const full = `${e.employee_name_first ?? ''} ${e.employee_name_last ?? ''}`.trim();
        map[e.employee_id] = full || e.employee_id;
      }
      setNames(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [key]);
  return names;
}

// ---------------------------------------------------------------------------
// Run B Phase 2 (2026-08-18) — the SET of service lines, for the AM entry
// screen. A sibling of saveProjectServiceLine(), not a second copy of it: the
// per-line write below is that same function, called once per line, so there is
// still exactly one place that knows the shape of a project_services row.
// ---------------------------------------------------------------------------

/**
 * Write several service lines against one project, in the order given.
 *
 * line_order is assigned from the array index so the invoice reads in the order
 * the AM typed. Lines are written one at a time rather than as a single array
 * insert because PostgREST reports a constraint failure against the batch, not
 * the row — and the constraint that fires here in practice is
 * project_services_other_requires_scope_text, where knowing WHICH line is the
 * whole of the useful error.
 */
export async function saveProjectServiceLines(
  projectId: string,
  lines: ProjectServiceLineInput[],
): Promise<string[]> {
  const ids: string[] = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    try {
      const id = await saveProjectServiceLine(projectId, {
        ...line,
        line_order: line.line_order ?? i + 1,
      });
      if (id) ids.push(id);
    } catch (err) {
      throw new Error(
        `Service line ${i + 1} didn't save: ${errorMessage(err, 'unknown error')}`,
      );
    }
  }
  return ids;
}

export interface NewProjectWithLinesInput {
  facility_id: string;
  account_id: string;
  /** 'Project' for billable service work, or one of the five special classes. */
  event_class: string;
  /** Required by a DB CHECK for every class except Project and Unclassified. */
  reason_code: string | null;
  /** Required by a DB CHECK for every class except Project. */
  reason_note: string | null;
  start_date: string | null;
  completion_date: string | null;
  due_date: string | null;
  bill_amount: number | null;
  pay_amount: number | null;
  special_pay: boolean;
  awaiting_schedule: boolean;
  notes: string | null;
}

/**
 * Create a project and all of its service lines.
 *
 * The project row is inserted first because project_id comes from a trigger and
 * the lines need it. If a line then fails, the project still exists and the
 * error is surfaced rather than swallowed — a half-written record the AM can
 * see and fix beats a silent success that recorded no service at all.
 *
 * assigned_partner_id is NOT set on the project row. Each line names and pays
 * its own sub (TCSD-0380, item #680, call 3): copying a line's sub up to the
 * project is a second copy of one fact. A billing-comms note with no line names
 * its sub later, on the project page, where that is the only place to put it.
 */
export async function createProjectWithLines(
  input: NewProjectWithLinesInput,
  lines: ProjectServiceLineInput[],
  createdByEid: string,
): Promise<{ project_id: string; service_line_ids: string[] }> {
  const payload: Record<string, unknown> = {
    facility_id: input.facility_id,
    account_id: input.account_id,
    event_class: input.event_class,
    reason_code: input.reason_code,
    reason_note: input.reason_note,
    start_date: input.start_date,
    completion_date: input.completion_date,
    due_date: input.due_date,
    bill_amount: input.bill_amount,
    pay_amount: input.pay_amount,
    special_pay: input.special_pay,
    awaiting_schedule: input.awaiting_schedule,
    notes: input.notes,
    created_by_eid: createdByEid,
    created_date: new Date().toISOString().slice(0, 10),
  };
  const { data, error } = await supabase
    .from('projects')
    .insert(payload)
    .select('project_id')
    .single();
  if (error) throw error;
  const projectId = (data as { project_id: string } | null)?.project_id;
  if (!projectId) throw new Error('The project saved but returned no id.');
  const service_line_ids = lines.length > 0 ? await saveProjectServiceLines(projectId, lines) : [];
  return { project_id: projectId, service_line_ids };
}

/**
 * "Repeat this one." Addendum part 2B, 2026-09-03.
 *
 * Reads a project and its service lines, and creates a fresh Draft carrying the
 * work but not this occurrence. The rules — what travels, what is dropped, and
 * why a chargeback is never copied — live in `lib/repeat-project.ts` with their
 * tests; this function only fetches and writes.
 *
 * ⛔ IT GOES THROUGH createProjectWithLines LIKE EVERY OTHER CREATE. A second
 * insert path that knew a different subset of the row is exactly how
 * saveProjectServiceLine started NULLing columns nobody named (TCSD, Run C
 * Phase 2). The DB mints the new project_id; nothing here supplies one
 * (TCSD-0142).
 */
export async function repeatProject(
  projectId: string,
  createdByEid: string,
): Promise<{ project_id: string }> {
  const { data: src, error: pErr } = await supabase
    .from('projects')
    .select('facility_id, account_id, event_class, reason_code, reason_note, notes')
    .eq('project_id', projectId)
    .single();
  if (pErr) throw pErr;
  if (!src) throw new Error('That project could not be read, so there is nothing to repeat.');

  const { data: lines, error: lErr } = await supabase
    .from('project_services')
    .select(
      'line_role, service_code, scope_text, scope_qty, client_rate, sub_rate, assigned_partner_id, line_order, notes',
    )
    .eq('project_id', projectId)
    .order('line_order', { ascending: true });
  if (lErr) throw lErr;

  const draft = buildRepeat(src as RepeatSource, (lines ?? []) as RepeatableLine[]);
  if (!draft) {
    throw new Error(
      'There is nothing to repeat on this record — it has no building, or every line on it was a chargeback.',
    );
  }

  const created = await createProjectWithLines(draft.project, draft.lines, createdByEid);
  return { project_id: created.project_id };
}
